import { useLang } from "@/context/LangContext";
import { MAP_PARTY_COLORS, NO_DATA_COLOR, OUTCOME_COLORS, OUTCOME_ORDER, partyLabel } from "@/lib/insights";

function partyRows(seats) {
  const c = {};
  for (const s of seats) {
    for (const [y, side] of [["y2021", s.y2021], ["y2026", s.y2026]]) {
      if (!side) continue;
      c[side.party_short] = c[side.party_short] || { party: side.party_short, y2021: 0, y2026: 0 };
      c[side.party_short][y] += 1;
    }
  }
  return Object.values(c).sort((a, b) => b.y2026 - a.y2026 || b.y2021 - a.y2021);
}

function PartyTable({ rows }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  return (
    <table className={`w-full text-[13px] ${ta ? "font-tamil" : ""}`} data-testid="district-party-table">
      <thead>
        <tr className="text-[11px] uppercase tracking-[0.12em] text-[#8c89ad]">
          <th className="text-left font-semibold pb-1.5">{t("col_party")}</th>
          <th className="text-right font-semibold pb-1.5 w-14">2021</th>
          <th className="text-right font-semibold pb-1.5 w-14">2026</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.party} className="border-t border-[#f0ecdf]" data-testid={`district-party-row-${r.party}`}>
            <td className="py-1.5 font-semibold text-[#1a1640]">
              <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ background: MAP_PARTY_COLORS[r.party] || NO_DATA_COLOR }} />
              {partyLabel(r.party, lang)}
            </td>
            <td className="py-1.5 text-right tabular-nums text-[#6b6894]">{r.y2021}</td>
            <td className="py-1.5 text-right tabular-nums font-bold text-[#1a1640]">{r.y2026}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DistrictSummary({ seats, selected, onPick }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  const d = seats[0];
  const outcomes = {};
  for (const s of seats) outcomes[s.outcome_key] = (outcomes[s.outcome_key] || 0) + 1;

  return (
    <section className="bg-white rounded-[22px] p-5 sm:p-6 card-in" data-testid="district-summary">
      <div className={`text-[11px] uppercase tracking-[0.2em] text-[#8c89ad] font-semibold ${ta ? "font-tamil" : ""}`}>{t("district_label")}</div>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className={`mt-1 text-2xl font-semibold text-[#1a1640] ${ta ? "font-tamil" : "font-display"}`} data-testid="district-summary-name">{ta ? d.district_ta : d.district}</h3>
        <span className={`text-[13px] font-semibold text-[#6b6894] whitespace-nowrap ${ta ? "font-tamil" : ""}`} data-testid="district-summary-count">{t("district_seats").replace("{n}", seats.length)}</span>
      </div>

      <div className={`mt-4 text-[13px] font-bold text-[#1a1640] ${ta ? "font-tamil" : ""}`}>{t("district_party_title")}</div>
      <div className="mt-2"><PartyTable rows={partyRows(seats)} /></div>
      <p className={`mt-1.5 text-[11px] text-[#8c89ad] ${ta ? "font-tamil" : ""}`}>{t("district_2021_note")}</p>

      <div className={`mt-4 text-[13px] font-bold text-[#1a1640] ${ta ? "font-tamil" : ""}`}>{t("district_changes")}</div>
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="district-outcomes">
        {OUTCOME_ORDER.filter((k) => outcomes[k]).map((k) => (
          <span key={k} className={`inline-flex items-center gap-1.5 rounded-full bg-[#f6f3ea] px-2.5 py-1 text-[12px] font-semibold text-[#1a1640] ${ta ? "font-tamil" : ""}`} data-testid={`district-outcome-${k}`}>
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: OUTCOME_COLORS[k] }} />
            {t(`outcome_${k}`)} <span className="tabular-nums text-[#6b6894]">{outcomes[k]}</span>
          </span>
        ))}
      </div>

      <div className={`mt-4 text-[13px] font-bold text-[#1a1640] ${ta ? "font-tamil" : ""}`}>{t("district_seat_list")}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {seats.map((s) => (
          <button
            key={s.ac_no}
            onClick={() => onPick(s.ac_no)}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold transition-colors ${selected === s.ac_no ? "bg-[#1a1640] text-white" : "bg-[#f6f3ea] text-[#1a1640] hover:bg-[#ece6d3]"} ${ta ? "font-tamil" : ""}`}
            data-testid={`district-seat-${s.ac_no}`}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: MAP_PARTY_COLORS[s.y2026?.party_short] || NO_DATA_COLOR }} />
            {ta ? s.name_ta : s.name}
          </button>
        ))}
      </div>
    </section>
  );
}
